import React, {
  useContext,
  useState,
  useMemo,
  useEffect,
  ChangeEvent,
  KeyboardEvent
} from 'react'
import * as yup from 'yup'
import styled from 'styled-components'
import { space, SpaceProps } from 'styled-system'
import * as queryString from 'query-string'
import debounce from 'lodash.debounce'
import { GridItem } from '@zuto/core/src/grid'
import MuiCurrencyInput from '@zuto/molecules/src/Mui/CurrencyInput/MuiCurrencyInput'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import QuestionComponentProps from '../../../Questions/question-component-props'
import LoanAmountValidator from '../../../validators/LoanAmountValidator'
import QuestionKey from '../../../Questions/question-key'
import { ApplicationFormContext } from '../../ApplicationForm/ApplicationFormContext'
import AppFormQuestion from '../../AppFormQuestion'
import {
  handlePrevButtonClick, returnDefaultLoanAmountValue, questionFontStyle, fireReactGAEvent
} from '../utils'
import NavigationButtons from '../../NavigationButtons/NavigationButtons'
import { AttributionContext } from '../../../context'
import { PrefillReviewPageApplicationFormContext } from '../../ApplicationForm/PrefillReviewPageApplicationFormContext'

const StyledDiv = styled.div<SpaceProps>`
  ${space}
`

const NOT_SURE_YET = 'Not sure yet'

export const LoanAmountQuestion = ({
  existingAnswers,
  disablePrevious,
  updateFollowingPrefillStep
}: QuestionComponentProps): JSX.Element => {
  const {
    loan_amount: loanAmountQuerySnakeCase,
    loanAmount: loanAmountQueryCamelCase
  } = queryString.parse(global.location.search)
  const { setQuestionName, hasNavigatedFromRequiredClick } = useContext(PrefillReviewPageApplicationFormContext)
  const attribution = useContext(AttributionContext)

  const validator = useMemo(() => LoanAmountValidator(attribution), [])
  const validationMessage = useMemo(() => validator.validationMessage, [validator])
  const validationSchema = useMemo(() => validator.getValidator(), [])

  const { dispatch, featureSwitches } = useContext(ApplicationFormContext)
  const { reviewPageV3, refinanceJourneyV1 } = featureSwitches ?? {}

  const [isNotSure, setIsNotSure] = useState(existingAnswers === NOT_SURE_YET)
  const [input, setInput] = useState(returnDefaultLoanAmountValue(existingAnswers ?? '', { loanAmountQuerySnakeCase, loanAmountQueryCamelCase }))
  const [isDirty, setIsDirty] = useState(!!input)
  const [showError, setShowError] = useState(isDirty)
  const [inputKey, setInputKey] = useState(0)

  const reviewPageV3AndClickedRequired = reviewPageV3 && hasNavigatedFromRequiredClick

  useEffect(() => {
    // Used for logging purposes in PrefillReviewPageApplicationForm since questionConfig is not available
    if (reviewPageV3) {
      setQuestionName('loanAmount')
    }
  }, [])

  const errorMessage: string = useMemo(() => {
    try {
      validationSchema.validateSync({ loanValue: input })
    } catch (untypedError: unknown) {
      const errorObject = untypedError as yup.ValidationError

      return errorObject.message
    }

    return ''
  }, [input])

  const debouncedSetShowError = useMemo(() => debounce(() => {
    setShowError(true)
    fireReactGAEvent({
      category: 'Application Form',
      action: 'Validation Error',
      label: 'Loan Amount',
      nonInteraction: true
    })
  }, 1500), [])

  useEffect(() => {
    if (!isNotSure && isDirty && errorMessage) {
      if (showError) {
        setShowError(true)
      } else {
        // We'll only debounce if no error is currently displayed.
        debouncedSetShowError()
      }
    } else {
      setShowError(false)
    }
  }, [input, isNotSure, isDirty, showError, errorMessage])

  const displayNextButton: boolean = useMemo(() => (isNotSure || (isDirty && !errorMessage)), [isNotSure, input, errorMessage])

  const canDisablePrevious = disablePrevious ? 'invisible' : undefined
  const canDisablePreviousForReviewFeature = reviewPageV3 ? !displayNextButton : canDisablePrevious

  const nextQuestion = () => {
    if (displayNextButton) {
      dispatch({
        type: 'QUESTION_ANSWERED',
        name: QuestionKey.LOAN_AMOUNT,
        value: isNotSure ? NOT_SURE_YET : input
      })
    }
    if (updateFollowingPrefillStep) {
      if (reviewPageV3AndClickedRequired || !hasNavigatedFromRequiredClick) {
        updateFollowingPrefillStep(true, true, false)
      }
    }
  }

  const previousQuestion = () => {
    handlePrevButtonClick(dispatch, {
      name: QuestionKey.LOAN_AMOUNT,
      value: isNotSure ? NOT_SURE_YET : input
    })
    if (updateFollowingPrefillStep) {
      if (reviewPageV3 && hasNavigatedFromRequiredClick) {
        updateFollowingPrefillStep(true, true, false)
      }
      if (!hasNavigatedFromRequiredClick) {
        updateFollowingPrefillStep(false, false, false)
      }
    }
  }

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    // TODO We should probably debounce this.
    if (isDirty) dispatch({ type: 'SAVE', name: QuestionKey.LOAN_AMOUNT, value: input })
    else setIsDirty(true)

    setInput(event.currentTarget.value)
  }

  const onKeyPress = (event: KeyboardEvent<HTMLInputElement | HTMLButtonElement>) => {
    if (event.code === 'Enter' || event.key === 'Enter') nextQuestion()
  }

  const toggleIsNotSure = () => {
    setInput('')

    // Update the key to force re-render (So that icon is no longer present when I\'m not sure is ticked)
    if (reviewPageV3) {
      setInputKey((prevKey) => prevKey + 1)
    }

    setIsNotSure((previous) => {
      const toggle = !previous

      if (toggle) {
        fireReactGAEvent({
          category: 'Application Form',
          action: 'Loan Amount',
          label: 'Unspecified'
        })
      } else {
        setIsDirty(false)
      }

      return toggle
    })
  }

  const tooltipDefault = "Don’t worry, the amount you enter is just a guide, it won’t affect whether you’re approved. You can change this later."
  const tooltipRefinance = "An estimate is fine if you don't know the exact figure."

  return (
    <>
      <GridItem gridColumn={['auto / span 12', '3 / span 8']}>
        <AppFormQuestion
          textStyle={questionFontStyle}
          questionKey={QuestionKey.LOAN_AMOUNT}
          mt={3}
          mb={2}
          hintText={refinanceJourneyV1 ? tooltipRefinance : tooltipDefault}
          showHintByDefault
        >
          { refinanceJourneyV1
            ?'Tell us about your current finance'
            : 'How much would you like to borrow?'
          }
        </AppFormQuestion>

        <StyledDiv mb={13}>
          <MuiCurrencyInput
            key={inputKey}
            adornmentLabel="£"
            adornmentShown
            darkerInput
            disabled={isNotSure}
            helperText={validationMessage}
            errorText={showError ? errorMessage : undefined}
            label={refinanceJourneyV1 ? "Please enter your settlement figure" : "Please enter an amount"}
            value={input}
            onChange={onChange}
            onKeyPress={onKeyPress}
          />
        </StyledDiv>
        {!refinanceJourneyV1 && (
          <StyledDiv mb={14}>     
            <FormControlLabel
              label="I'm not sure yet"
              onChange={toggleIsNotSure}
              control={(
                <Checkbox
                  checked={isNotSure}
                  data-testid="not-sure-checkbox"
                  onKeyPress={onKeyPress}
                />
              )}
            />
          </StyledDiv>
        )}
      </GridItem>
      <NavigationButtons
        nextHandler={nextQuestion}
        previousHandler={previousQuestion}
        disableNext={!displayNextButton}
        disablePrevious={canDisablePreviousForReviewFeature}
      />
    </>
  )
}
